'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';

import { useDeviceCapabilities } from '@/hooks/use-device-capabilities';
import { trpcVanilla } from '@/lib/trpc';

import { useActiveCampaignClosed } from '../hooks/use-public-lookups';
import { useRegistrationAuth } from '../hooks/useRegistrationAuth';
import { AuthStep } from '../components/registration/AuthStep';
import { useRegistrationStore } from '../stores/registration-store';
import { trackRego } from '../lib/registration-analytics';
import { useRegistrationInit } from './hooks/useRegistrationInit';
import { useCheckoutStatePersistence } from './hooks/useCheckoutStatePersistence';
import { useCheckoutReturn } from './hooks/useCheckoutReturn';
import { parseHostedCheckoutReturn } from './lib/hostedCheckoutReturn';
import { StepIndicator } from './components/StepIndicator';
import { NavigationBar } from './components/NavigationBar';
import { CompletionScreen } from './components/CompletionScreen';
import { AwaitingPaymentScreen } from './components/AwaitingPaymentScreen';
import { RideSelectionStep, type RideSelectionMeta } from './steps/RideSelectionStep';
import { PreferencesStep } from './steps/PreferencesStep';
import { getInitialCountryFromParams, parseRideMinimumDonation } from './lib/rideSelection';
import type {
  CheckoutReturnFailure,
  PrefetchedRegistrationData,
  RegistrationCompletionResult,
  RegistrationEntrantSummary,
  RegistrationPhase,
  TenantRegistrationConfig,
} from './types';

const EMPTY_RIDE_META: RideSelectionMeta = {
  hasStates: false,
  isFull: false,
  isCancelled: false,
  minimumDonation: 0,
};

interface RegistrationFlowProps {
  config: TenantRegistrationConfig;
  prefetchedData?: PrefetchedRegistrationData;
}

/**
 * Registration step orchestrator: owns step/phase state, ride selection,
 * validation gates and navigation chrome. Payment specifics live in
 * config.CheckoutStep; per-step UI lives in steps/.
 */
export function RegistrationFlow({ config, prefetchedData }: RegistrationFlowProps) {
  const t = useTranslations('register');
  const tCommon = useTranslations('common');
  const tDonate = useTranslations('donate');
  const tRoot = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isKeyboardOpen } = useDeviceCapabilities();

  // ── Checkout-return params, captured ONCE at mount: the return hook cleans
  // them off the URL via history.replaceState, which re-syncs useSearchParams —
  // deriving these reactively would flip init mid-flight. ──
  const [checkoutReturn] = useState(() => parseHostedCheckoutReturn(searchParams));
  const [returnEventId] = useState(() =>
    checkoutReturn.kind === 'success' ? searchParams.get('eventId') : null,
  );
  const isCheckoutSuccessReturn = checkoutReturn.kind === 'success';

  const initialCountryFromUrl = getInitialCountryFromParams(searchParams);
  const urlEventId = searchParams.get('eventId') ?? '';

  const init = useRegistrationInit({
    initialCountryFromUrl,
    detectedCountry: prefetchedData?.detectedCountry,
    isRedirectReturn: isCheckoutSuccessReturn,
  });

  // Closed campaigns keep their events listed on step 1 (when the tenant
  // enables events.show_closed_campaign_events) — registration must still be
  // blocked here, with the server rejecting ensureRegistration as backstop.
  const { isCampaignClosed, closedMessage: campaignClosedMessage } = useActiveCampaignClosed();

  const auth = useRegistrationAuth({ vehicleType: config.vehicleType });
  // The interactive auth step sets `auth.entrant`; on a resume path (refresh,
  // "Finish your registration", redirect-return) it never mounts, so fall back
  // to the entrant hydrated from the authenticated session. Without this the
  // step-4 Pay button silently no-ops (entrant null → startCheckout returns).
  const entrant: RegistrationEntrantSummary | null = auth.entrant ?? init.resumeEntrant;
  const vehicle = auth.vehicle;

  // ── Flow state ──
  const [phase, setPhase] = useState<RegistrationPhase>('form');
  const [currentStep, setCurrentStep] = useState(1);
  const [rideCountryCode, setRideCountryCode] = useState(
    initialCountryFromUrl || prefetchedData?.detectedCountry || '',
  );
  const [rideStateCode, setRideStateCode] = useState(searchParams.get('state') ?? '');
  const [rideId, setRideId] = useState('');
  const [rideName, setRideName] = useState('');
  const [rideMeta, setRideMeta] = useState<RideSelectionMeta>(EMPTY_RIDE_META);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [joinWaitlist, setJoinWaitlist] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completion, setCompletion] = useState<RegistrationCompletionResult | null>(null);
  const [returnFailure, setReturnFailure] = useState<CheckoutReturnFailure | null>(null);

  // Checkout step reports validity/loading/labels and registers its handlers
  const [checkoutCanSubmit, setCheckoutCanSubmit] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutNextLabel, setCheckoutNextLabel] = useState<string | null>(null);
  const [navHidden] = useState(false);
  const checkoutSubmitRef = useRef<(() => Promise<void>) | null>(null);
  const registerCheckoutSubmit = useCallback((fn: (() => Promise<void>) | null) => {
    checkoutSubmitRef.current = fn;
  }, []);
  const [checkoutSkip, setCheckoutSkip] = useState<{ fn: () => void } | null>(null);
  const registerCheckoutSkip = useCallback((fn: (() => void) | null) => {
    setCheckoutSkip(fn ? { fn } : null);
  }, []);

  const newsletterOptIn = useRegistrationStore((s) => s.newsletterOptIn);
  const marketingOptIns = useRegistrationStore((s) => s.marketingOptIns);
  const hydrateFromServer = useRegistrationStore((s) => s.hydrateFromServer);

  // ── URL sync for shareable ride selection ──
  const updateRideUrl = useCallback(
    (country: string, state: string, eventSlug: string) => {
      const params = new URLSearchParams(searchParams.toString());
      if (country) params.set('country', country);
      else params.delete('country');
      if (state) params.set('state', state);
      else params.delete('state');
      if (eventSlug) params.set('eventId', eventSlug);
      else params.delete('eventId');
      params.delete('intent');

      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  // ── Cascading ride setters ──
  const setRideCountry = useCallback(
    (code: string) => {
      setRideCountryCode(code);
      setRideStateCode('');
      setRideId('');
      setRideName('');
      setJoinWaitlist(false);
      updateRideUrl(code, '', '');
    },
    [updateRideUrl],
  );

  const setRideState = useCallback(
    (code: string) => {
      setRideStateCode(code);
      setRideId('');
      setRideName('');
      setJoinWaitlist(false);
      updateRideUrl(rideCountryCode, code, '');
    },
    [rideCountryCode, updateRideUrl],
  );

  const setRide = useCallback(
    (id: string, name: string, eventSlug = '') => {
      setRideId(id);
      setRideName(name);
      setJoinWaitlist(false);
      updateRideUrl(rideCountryCode, rideStateCode, eventSlug);
    },
    [rideCountryCode, rideStateCode, updateRideUrl],
  );

  // ── Apply init state when auth + guard resolve. Step navigation re-applies
  // when init.initialStep changes; checkout state is only hydrated ONCE so a
  // late router.refresh() can't overwrite user edits with stale server state. ──
  const lastAppliedStep = useRef<number | null>(null);
  const hasHydratedCheckout = useRef(false);
  useEffect(() => {
    if (init.isLoading) return;
    if (lastAppliedStep.current === init.initialStep) return;
    lastAppliedStep.current = init.initialStep;

    if (init.isBlocked) {
      setPhase('blocked');
      return;
    }
    setCurrentStep(init.initialStep);

    if (init.pendingRegistration) {
      const intent = searchParams.get('intent');
      setRideCountryCode(init.pendingRegistration.countryCode);
      setRideStateCode(init.pendingRegistration.stateCode ?? '');

      if (intent === 'changeRide') {
        setRideId('');
        setRideName('');
        setJoinWaitlist(false);
        updateRideUrl(
          init.pendingRegistration.countryCode,
          init.pendingRegistration.stateCode ?? '',
          '',
        );
      } else {
        setRideId(init.pendingRegistration.eventId);
        setRideName(init.pendingRegistration.eventName ?? '');
        // Seed the donation gate from server-backed state so step-4 gating is
        // correct when resuming directly into step 3/4 (RideSelectionStep, which
        // otherwise derives this, never mounts). It re-derives if the user returns
        // to step 1.
        const seededMinimumDonation = parseRideMinimumDonation(
          init.pendingRegistration.minimumDonation,
        );
        setRideMeta((prev) => ({ ...prev, minimumDonation: seededMinimumDonation }));
        updateRideUrl(
          init.pendingRegistration.countryCode,
          init.pendingRegistration.stateCode ?? '',
          urlEventId,
        );
        if (init.pendingRegistration.checkoutState && !hasHydratedCheckout.current) {
          hasHydratedCheckout.current = true;
          hydrateFromServer(init.pendingRegistration.checkoutState);
        }
      }
    }
  }, [
    init.isLoading,
    init.isBlocked,
    init.initialStep,
    init.pendingRegistration,
    searchParams,
    updateRideUrl,
    urlEventId,
    hydrateFromServer,
  ]);

  // ── Server persistence of the steps-3/4 form buffer ──
  const { flush: flushCheckoutState } = useCheckoutStatePersistence({
    enabled: Boolean(init.authUser) && phase === 'form',
    eventId: rideId || null,
    currentStep,
  });

  // ── Step gates ──
  const shouldSkipPaymentStep = rideMeta.isFull && joinWaitlist;
  const isStep1Valid =
    Boolean(rideCountryCode && rideId) &&
    waiverAccepted &&
    (!rideMeta.hasStates || Boolean(rideStateCode)) &&
    (!rideMeta.isFull || joinWaitlist) &&
    !rideMeta.isCancelled;
  const isStep2Valid = Boolean(entrant);
  const canProceed =
    !isCampaignClosed &&
    ((currentStep === 1 && isStep1Valid) ||
      (currentStep === 2 && isStep2Valid) ||
      currentStep === 3 ||
      (currentStep === 4 && checkoutCanSubmit));

  // ── Step viewed analytics ──
  useEffect(() => {
    const stepNames = ['ride_selection', 'auth', 'preferences', 'payment'] as const;
    const stepName = stepNames[currentStep - 1];
    if (stepName) trackRego('rego_step_viewed', { step: currentStep, step_name: stepName });
  }, [currentStep]);

  // ── Waitlist completion (no payment step) ──
  const completeWaitlistRegistration = useCallback(async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await trpcVanilla.publicEntrant.ensureRegistration.mutate({
        eventId: rideId,
        joinWaitlist: true,
        defaultBlog: { title: t('defaultBlogTitle'), content: t('defaultBlogContent') },
      });
      await trpcVanilla.publicEntrant.completeRegistration.mutate({
        eventId: rideId,
        termsAccepted: true,
        newsletterOptIn,
        marketingOptIns,
      });
      trackRego('rego_completed', { is_waitlist: true, skipped_donation: true });
      setCompletion({ email: entrant?.email ?? '', donationId: null, isWaitlist: true });
      setPhase('complete');
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : t('registrationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [rideId, newsletterOptIn, marketingOptIns, entrant?.email, t]);

  // ── Navigation ──
  // Returning entrants skip the auth step entirely (1 ↔ 3 jumps both ways).
  // The forward jump must ensureRegistration first — checkout-state saves and
  // checkout scope resolution need the entrant_history row that the auth step
  // would otherwise have created.
  const shouldSkipAuthStep = init.shouldSkipAuthStep;
  const skipAuthContinue = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await trpcVanilla.publicEntrant.ensureRegistration.mutate({
        eventId: rideId,
        joinWaitlist,
        defaultBlog: { title: t('defaultBlogTitle'), content: t('defaultBlogContent') },
      });
      setCurrentStep(3);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : t('registrationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [rideId, joinWaitlist, t]);

  const handleNext = useCallback(() => {
    setSubmitError(null);
    if (currentStep === 1) {
      if (shouldSkipAuthStep) {
        void skipAuthContinue();
      } else {
        setCurrentStep(2);
      }
      return;
    }
    if (currentStep === 2) {
      setCurrentStep(3);
      return;
    }
    if (currentStep === 3) {
      if (shouldSkipPaymentStep) {
        void completeWaitlistRegistration();
      } else {
        setCurrentStep(4);
      }
      return;
    }
    void checkoutSubmitRef.current?.();
  }, [
    currentStep,
    shouldSkipAuthStep,
    skipAuthContinue,
    shouldSkipPaymentStep,
    completeWaitlistRegistration,
  ]);

  const handleBack = useCallback(() => {
    setSubmitError(null);
    setCurrentStep((step) => {
      if (step === 3 && shouldSkipAuthStep) return 1;
      return Math.max(1, step - 1);
    });
  }, [shouldSkipAuthStep]);

  const handleStepClick = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const handleAuthStepSuccess = useCallback(() => {
    router.refresh();
    setCurrentStep(3);
  }, [router]);

  // ── Checkout outcome callbacks ──
  const handleCheckoutCompleted = useCallback((result: RegistrationCompletionResult) => {
    setCompletion(result);
    setPhase('complete');
  }, []);
  const handleAwaitingPayment = useCallback(() => {
    setPhase('awaiting-payment');
  }, []);
  const handleReturnFailure = useCallback((failure: CheckoutReturnFailure) => {
    setReturnFailure(failure);
  }, []);

  // ── Checkout return: clean URL, poll settlement, complete or restore ──
  const { isSettling } = useCheckoutReturn({
    checkoutReturn,
    eventId: returnEventId ?? (rideId || null),
    entrantEmail: entrant?.email ?? null,
    newsletterOptIn,
    marketingOptIns,
    onCompleted: handleCheckoutCompleted,
    onAwaitingPayment: handleAwaitingPayment,
    onReturnFailure: handleReturnFailure,
  });

  // ── Phases outside the form ──
  if (init.isLoading || isSettling) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        {isSettling && (
          <div className="space-y-1 text-center">
            <p className="font-medium text-foreground">{tDonate('form.awaitingSettlement')}</p>
            <p className="text-sm text-muted-foreground">
              {tDonate('form.awaitingSettlementMessage')}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (phase === 'complete' && completion) {
    return (
      <CompletionScreen
        email={completion.email}
        donationId={completion.donationId}
        tenantSlug={config.tenantSlug}
      />
    );
  }

  if (phase === 'awaiting-payment') {
    return <AwaitingPaymentScreen email={entrant?.email ?? null} />;
  }

  // ── Form ──
  const stepLabel =
    currentStep === 1
      ? t('chooseYourRide')
      : currentStep === 2
        ? t('yourDetails')
        : currentStep === 3
          ? t('terms')
          : t('payment');

  const eventDateLabel = config.eventDateKey ? tRoot(config.eventDateKey) : null;
  const CheckoutStep = config.CheckoutStep;

  return (
    <div className="flex flex-col">
      <StepIndicator
        currentStep={currentStep}
        stepLabel={stepLabel}
        hidden={navHidden}
        onStepClick={handleStepClick}
        navigationDisabled={isSubmitting || checkoutLoading}
      />

      <div className="flex-1 py-6 pb-32 md:pb-6">
        {(currentStep === 1 || phase === 'blocked') && (
          <RideSelectionStep
            config={config}
            prefetchedData={prefetchedData}
            isBlocked={phase === 'blocked'}
            isCampaignClosed={isCampaignClosed}
            campaignClosedMessage={campaignClosedMessage}
            countryCode={rideCountryCode}
            stateCode={rideStateCode}
            rideId={rideId}
            rideName={rideName}
            waiverAccepted={waiverAccepted}
            joinWaitlist={joinWaitlist}
            onCountryChange={setRideCountry}
            onStateChange={setRideState}
            onRideChange={setRide}
            onWaiverChange={setWaiverAccepted}
            onWaitlistChange={setJoinWaitlist}
            onMetaChange={setRideMeta}
          />
        )}

        {currentStep === 2 && phase === 'form' && (
          <AuthStep
            cityName={rideName || undefined}
            onSuccess={handleAuthStepSuccess}
            onBack={() => setCurrentStep(1)}
            eventId={rideId}
            joinWaitlist={joinWaitlist}
            countryCode={rideCountryCode}
            auth={auth}
            authenticatedProfile={init.authenticatedProfile}
            vehicleType={config.vehicleType}
            theme="semantic"
          />
        )}

        {currentStep === 3 && phase === 'form' && (
          <PreferencesStep
            config={config}
            eventId={rideId || null}
            shouldSkipPaymentStep={shouldSkipPaymentStep}
            summary={{
              rideName,
              countryCode: rideCountryCode,
              stateCode: rideStateCode,
              eventDateLabel,
              entrant,
              vehicle,
              onChangeRide: () => setCurrentStep(1),
            }}
          />
        )}

        {currentStep === 4 && phase === 'form' && (
          <CheckoutStep
            config={config}
            ride={{
              eventId: rideId,
              eventName: rideName || null,
              countryCode: rideCountryCode,
              minimumDonation: rideMeta.minimumDonation,
              joinWaitlist,
            }}
            entrant={entrant}
            onCompleted={handleCheckoutCompleted}
            onAwaitingPayment={handleAwaitingPayment}
            onError={setSubmitError}
            registerSubmit={registerCheckoutSubmit}
            registerSkip={registerCheckoutSkip}
            setNavValidity={setCheckoutCanSubmit}
            setNavLoading={setCheckoutLoading}
            setNavLabel={setCheckoutNextLabel}
            flushCheckoutState={flushCheckoutState}
            returnFailure={returnFailure}
          />
        )}

        {submitError && (
          <p className="mt-4 text-sm text-destructive" role="alert">
            {submitError}
          </p>
        )}
      </div>

      {/* Hidden on step 2 — AuthStep submits via its own form actions */}
      <NavigationBar
        currentStep={currentStep}
        canProceed={canProceed && !isSubmitting && phase === 'form'}
        isLoading={isSubmitting || checkoutLoading}
        onNext={handleNext}
        onBack={handleBack}
        nextLabel={
          currentStep === 3 && shouldSkipPaymentStep
            ? t('joinWaitlistButton')
            : (checkoutNextLabel ?? tCommon('continue'))
        }
        backLabel={tCommon('back')}
        loadingLabel={tCommon('loading')}
        showBack={currentStep > 1 && phase === 'form'}
        hidden={navHidden || currentStep === 2 || phase === 'blocked'}
        isKeyboardOpen={isKeyboardOpen}
        stepLabel={stepLabel}
        onSkip={currentStep === 4 && checkoutSkip ? checkoutSkip.fn : undefined}
        skipLabel={tDonate('skipAndComplete')}
        skipLabelMobile={tDonate('skip')}
      />
    </div>
  );
}
